/**
 * Decision 0135 (lesson 2, "nothing fails silently"): what a run's end means for the people and agents around it.
 * Facts and labels only; the retry itself, the notice and the wake live in the cloud, and what to do about a failure is
 * the agent's judgement. Pure.
 */
import type { StopReason } from "@minga/protocol"
import { isRunEnded } from "./state"
import type { AgentId, Ids, ProjectState, Run } from "./types"

type UserId = Ids["UserId"]

/** What happens after a failed turn, as its chat notice says it. */
export type TurnEndNext = "retrying_at" | "lead_decides" | "fix_setup" | "send_again"

/** The facts of one turn end the body has (TurnRunEnded). */
export interface TurnEndFacts {
  readonly stopReason: StopReason
  readonly errorCode: string | null
  readonly errorMessage: string | null
  /** The run started before it ended; null = not known (an older Project). */
  readonly started: boolean | null
}

/** A provider that is down or overloaded for a moment (HTTP 5xx, Anthropic's 529 "overloaded"). */
export const providerOverloaded = (code: string | null, message: string | null): boolean =>
  /^http_5\d\d$/.test(code ?? "") || code === "provider_overloaded" || /\boverloaded\b/i.test(message ?? "")

// ---- provider errors in words (cloud minds and runner CLIs) ----------------------------------------------------------

/**
 * Decision 0135: the provider says the account behind the key has no credit left (Anthropic's 400 "credit balance is
 * too low", OpenAI's 429 `insufficient_quota`). A person tops it up; no retry helps.
 */
export const CREDIT_EXHAUSTED_TEXT =
  /credit balance is too low|insufficient_quota|exceeded your current quota|purchase credits/i
/** A key or login the provider refused: 401/403, a bad key, a CLI that is signed out or whose OAuth token expired. */
const AUTH_TEXT =
  /\b(?:status|http|code|error|api error)[ :=]*40[13]\b|invalid[ _-]?(?:x-)?api[ _-]?key|authentication_error|authentication failed|not logged in|please run \/login|oauth token (?:has )?expired|token has expired/i
/** A rate or usage limit (429). Checked after the credit text, which also comes as a 429. */
const RATE_LIMIT_TEXT =
  /\b(?:status|http|code|error|api error)[ :=]*429\b|rate[ _-]?limit|usage limit|too many requests/i
/** The provider is overloaded or failing for a moment: Anthropic's 529, any 5xx, the usual gateway words. */
const OVERLOADED_TEXT =
  /\boverloaded\b|\b(?:status|http|code|error|api error)[ :=]*5\d\d\b|internal server error|service unavailable|bad gateway|gateway time-?out/i

/** How a provider error in words ends a turn, with the error code the cloud's retry and banner logic reads. */
export type ProviderErrorClass =
  | { readonly stopReason: "budget_exhausted"; readonly code: "credit_exhausted" }
  | { readonly stopReason: "auth_error"; readonly code: "auth_error" }
  | { readonly stopReason: "rate_limited"; readonly code: "rate_limited" }
  | { readonly stopReason: "cli_error"; readonly code: "provider_overloaded" }

/**
 * Decision 0135, for errors that arrive as text (a runner CLI's result or stderr, a thrown provider error): credit
 * first (OpenAI's comes as a 429), then auth, a rate limit, an overloaded provider; null when the text says none of
 * them. The cloud's `transientTurnEnd` retries `provider_overloaded`; `credit_exhausted` raises the setup banner.
 */
export const classifyProviderText = (text: string | null | undefined): ProviderErrorClass | null => {
  const t = text ?? ""
  if (!t) return null
  if (CREDIT_EXHAUSTED_TEXT.test(t)) return { stopReason: "budget_exhausted", code: "credit_exhausted" }
  if (AUTH_TEXT.test(t)) return { stopReason: "auth_error", code: "auth_error" }
  if (RATE_LIMIT_TEXT.test(t)) return { stopReason: "rate_limited", code: "rate_limited" }
  if (OVERLOADED_TEXT.test(t)) return { stopReason: "cli_error", code: "provider_overloaded" }
  return null
}

/**
 * A turn end that may pass by itself, so the body retries the turn once: a lost runner, Minga's MCP server that did
 * not connect, a sandbox that could not start, a provider that was overloaded, a rate limit (at its reset).
 */
export const transientTurnEnd = (f: TurnEndFacts): boolean =>
  f.stopReason === "runner_lost" ||
  f.stopReason === "mcp_unavailable" ||
  f.stopReason === "rate_limited" ||
  (f.stopReason === "sandbox_error" && f.started !== true) ||
  (f.stopReason === "cli_error" && providerOverloaded(f.errorCode, f.errorMessage))

/** A turn end a person fixes in settings (a key refused or missing, a signed-out CLI, an account out of credit). */
export const setupTurnEnd = (f: Pick<TurnEndFacts, "stopReason">): boolean =>
  f.stopReason === "auth_error" || f.stopReason === "budget_exhausted"

/** The next step a failed turn's notice names. */
export const turnEndNext = (o: {
  readonly setup: boolean
  readonly willRetry: boolean
  readonly askedByPerson: boolean
}): TurnEndNext =>
  o.setup ? "fix_setup" : o.willRetry ? "retrying_at" : o.askedByPerson ? "send_again" : "lead_decides"

/** Ends that are someone's own action or no attempt at all: never a failure (a stop, a newer epoch, never started). */
const NOT_A_FAILURE: ReadonlySet<StopReason> = new Set([
  "completed",
  "interrupted",
  "superseded",
  "cancelled",
])

/**
 * A worker or validator that ended `completed` without saying how it went: no outcome (a worker) or no verdict (a
 * validator). A view-level label ("ended without a report"), not a stop reason: the run ended on its own.
 */
export const endedWithoutReport = (r: Run): boolean => {
  // the stop reason is set when the run ends (read first: views pass partial runs)
  if (r.stopReason !== "completed") return false
  if (r.kind === "validator") return r.verdict === null
  if (r.kind === "worker") {
    const o = r.outcome ?? r.claimed
    return o === null || o._tag === "unreported"
  }
  return false
}

/** A run that didn't work: it ended any way but completed (or a stop of its own), or a worker without a report. */
export const runFailed = (r: Run): boolean =>
  isRunEnded(r) && ((r.stopReason !== null && !NOT_A_FAILURE.has(r.stopReason)) || endedWithoutReport(r))

/** How a failed run ended, in a few words for a turn file or a brief line ("cli_error (http_400)"). */
export const failureWords = (r: Run): string => {
  if (endedWithoutReport(r)) return "ended without a report"
  const code = r.error?.code && r.error.code !== r.stopReason ? ` (${r.error.code})` : ""
  return `${r.stopReason ?? "ended"}${code}`
}

// ---- quiet runs ---------------------------------------------------------------------------------------------------

/** A live worker or validator is quiet when its CLI produced nothing for this long (heartbeats' `idleMs`). */
export const QUIET_RUN_MS = 20 * 60_000

/** The heartbeat facts the Project DO keeps per live run (`RunActivity`). */
export interface QuietFacts {
  readonly phase: string
  readonly idleMs: number
  /** Time of the newest content frame. */
  readonly at: number
}

/**
 * Is the watchdog due to note this run quiet? A started worker or validator, running (not waiting on a permission: a
 * person holds that), idle past {@link QUIET_RUN_MS}, and not noted already in this quiet stretch (no output since).
 */
export const quietRunDue = (r: Run, a: QuietFacts): boolean =>
  (r.kind === "worker" || r.kind === "validator") &&
  r.state._tag === "leased" &&
  r.state.phase === "running" &&
  a.phase === "running" &&
  a.idleMs >= QUIET_RUN_MS &&
  (r.quietAt === undefined || a.at > r.quietAt)

/** The agent a quiet run wakes: its own agent (the item's owner agent), else the lead. */
export const quietRunAgent = (s: ProjectState, r: Run): AgentId | null =>
  r.agentId ?? s.project?.leadAgentId ?? null

const minutes = (ms: number): string => `${Math.max(1, Math.round(ms / 60_000))} min`

/** The wake line of a quiet run: "W-4 on DIC-6 has produced nothing for 25 min. Wait, steer or stop it." */
export const quietRunLine = (s: ProjectState, r: Run, idleMs: number): string => {
  const item = r.itemId ? s.items.find((t) => t.id === r.itemId) : undefined
  const who = r.worker?.ref ?? r.ref
  return `${who}${item ? ` on ${item.ref}` : ""} has produced nothing for ${minutes(idleMs)}. Look at it (read_run), then wait, steer or stop it.`
}

// ---- what didn't work, for an agent and for a person -----------------------------------------------------------------

const HOUR = 3_600_000
/** How far back a lead's turn file looks for its own trouble. */
export const TROUBLE_WINDOW_MS = 24 * HOUR
/** At most this many trouble lines in a turn file. */
const TROUBLE_MAX = 6

const ago = (ms: number): string => {
  const m = Math.round(ms / 60_000)
  return m < 60 ? `${Math.max(1, m)} min ago` : `${Math.round(m / 60)} h ago`
}

/**
 * Decision 0135: an agent's own recent trouble, newest first: its turns that failed and the runs it supervises that
 * failed, ended without a report, or are quiet right now. Lines for its turn file; what to do (re-route, pause, wait,
 * ask) is its call.
 */
export const recentTroubles = (
  s: ProjectState,
  agentId: AgentId,
  now: number,
  /** Its runs (the store keeps more than the state); default the state's. */
  runs: ReadonlyArray<Run> = s.runs,
): ReadonlyArray<string> => {
  const rows: Array<{ at: number; line: string }> = []
  const seen = new Set<string>()
  for (const r of runs) {
    if (seen.has(r.id)) continue
    seen.add(r.id)
    if (r.agentId !== agentId) continue
    if (!isRunEnded(r)) {
      if (r.quietAt !== undefined && now - r.quietAt <= TROUBLE_WINDOW_MS)
        rows.push({
          at: r.quietAt,
          line: `${r.worker?.ref ?? r.ref} went quiet ${ago(now - r.quietAt)} and is still live`,
        })
      continue
    }
    const at = r.endedAt ?? r.requestedAt
    if (now - at > TROUBLE_WINDOW_MS || !runFailed(r)) continue
    const who = r.kind === "turn" ? `your turn ${r.ref}` : (r.worker?.ref ?? r.ref)
    const item = r.itemId ? s.items.find((t) => t.id === r.itemId) : undefined
    rows.push({ at, line: `${who}${item ? ` on ${item.ref}` : ""}: ${failureWords(r)}, ${ago(now - at)}` })
  }
  return rows
    .sort((a, b) => b.at - a.at)
    .slice(0, TROUBLE_MAX)
    .map((r) => r.line)
}

/**
 * Decision 0135: the runs that didn't work since `since` and concern this person: turns they asked for, and workers
 * and validators on items they asked for or own. Refs, newest first.
 */
export const failedRunsFor = (s: ProjectState, userId: UserId, since: number): ReadonlyArray<string> => {
  const theirs = (r: Run): boolean => {
    if (r.kind === "turn") return r.askedBy?._tag === "user" && r.askedBy.userId === userId
    const t = r.itemId ? s.items.find((x) => x.id === r.itemId) : undefined
    if (!t) return false
    return (
      (t.askedBy?._tag === "user" && t.askedBy.userId === userId) ||
      (t.createdBy._tag === "user" && t.createdBy.userId === userId) ||
      (t.owner?._tag === "user" && t.owner.userId === userId)
    )
  }
  return s.runs
    .filter((r) => runFailed(r) && (r.endedAt ?? 0) > since && theirs(r))
    .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
    .map((r) => r.worker?.ref ?? r.ref)
}

/**
 * Decision 0135 (lesson 3): the ref of the agent's last turn when it ended asking in prose (the body says so) and
 * opened no card in that turn; else null. One nudge in the next turn file, no re-wake.
 */
export const proseQuestionOf = (
  s: ProjectState,
  agentId: AgentId,
  runId: string | null,
  storedRun: (id: string) => Run | null | undefined = () => undefined,
): string | null => {
  if (runId === null) return null
  const r = s.runs.find((x) => x.id === runId) ?? storedRun(runId) ?? undefined
  if (!r || r.kind !== "turn" || r.agentId !== agentId) return null
  const carded = s.proposals.some(
    (p) => (p.proposedBy._tag === "agent" || p.proposedBy._tag === "worker") && p.proposedBy.runId === r.id,
  )
  return carded ? null : r.ref
}
